import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { transactionService } from '../services/transactionService';
import AccountSelectionModal from '../components/AccountSelectionModal';
import type { PublicDepositTransaction } from '../types/api';

interface BankOption {
  id: string;
  name: string;
  displayName: string;
  description: string;
  icon: string;
}

const BANK_OPTIONS: BankOption[] = [
  {
    id: 'maybankfpx',
    name: 'maybankfpx',
    displayName: 'Maybank FPX',
    description: 'Transfer from your Maybank account',
    icon: '🏦'
  },
  // {
  //   id: 'pbefpx',
  //   name: 'pbefpx',
  //   displayName: 'Public Bank FPX',
  //   description: 'Transfer from your Public Bank account',
  //   icon: '🏛️'
  // }
  {
    id: 'rhbfpx',
    name: 'rhbfpx',
    displayName: 'RHB Bank FPX',
    description: 'Transfer from your RHB Bank account',
    icon: '🏦'
  },
];

const DepositPage: React.FC = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [transaction, setTransaction] = useState<PublicDepositTransaction | null>(null);
  const [selectedBank, setSelectedBank] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [step, setStep] = useState<'bank_selection' | 'login_form' | 'processing' | 'account_selection' | 'waiting_approval' | 'completed' | 'failed'>('bank_selection');
  const [showAccountModal, setShowAccountModal] = useState<boolean>(false);
  const [isAccountSelectionLoading, setIsAccountSelectionLoading] = useState<boolean>(false);
  const [accountSelectionSubmitted, setAccountSelectionSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (transactionId) {
      fetchTransaction();
    }
  }, [transactionId]);

  useEffect(() => {
    // Check for bank parameter in URL
    const bankParam = searchParams.get('bank');
    if (bankParam && BANK_OPTIONS.find(bank => bank.id === bankParam)) {
      setSelectedBank(bankParam);
      setStep('login_form');
    }
  }, [searchParams]);

  // Helper to check if status indicates account selection
  const isAccountSelectionState = (status?: string) => (
    status === 'selectaccount' || status === 'account_selection_required' || status === 'SELECTACCOUNT'
  );

  // Helper to check if status is post-selection or terminal (should not re-open selection modal)
  const isPostSelectionOrTerminalState = (status?: string) => {
    const s = String(status || '').toLowerCase();
    return s === 'waitapproval' || s === 'processing' || s === 'done' || s === 'completed' || s === 'success' || s === 'failed' || s === 'rejected' || s === 'timeout';
  };

  // Watch for transaction changes and handle account selection
  useEffect(() => {
    if (!transaction) return;

    // Don't re-open modal if user already submitted their selection
    if (accountSelectionSubmitted) {
      console.log('🔒 Account selection already submitted, skipping modal re-open');
      return;
    }

    // Primary: open when server marks selection required
    if (isAccountSelectionState(transaction.statusTxt)) {
      if (!showAccountModal || step !== 'account_selection') {
        console.log('🏦 Transaction updated: Account selection required, showing modal with accounts:', transaction.accountList);
        setStep('account_selection');
        setShowAccountModal(true);
      }
      return;
    }

    // Fallback for Maybank: if accounts arrived but status update lags slightly
    // Only trigger if we're already in processing step (user has submitted credentials)
    if (
      step === 'processing' &&
      (transaction.bank === 'maybankfpx') &&
      Array.isArray(transaction.accountList) && transaction.accountList.length > 0 &&
      !showAccountModal &&
      !isPostSelectionOrTerminalState(transaction.statusTxt)
    ) {
      console.log('🏦 Maybank fallback: account list present, opening selection modal while waiting for status flip');
      setStep('account_selection');
      setShowAccountModal(true);
    }
  }, [transaction, showAccountModal, step, accountSelectionSubmitted]);

  // Watch for step changes and start polling when processing
  useEffect(() => {
    if (step === 'processing' && transaction) {
      console.log('🔄 Step changed to processing, starting transaction polling');
      startTransactionPolling();
    }
  }, [step, transaction]);

  const fetchTransaction = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await transactionService.getDepositTransaction(transactionId!);

      if (response.success && response.data) {
        console.log('🏦 Deposit Page: Transaction data received:', response.data);
        console.log('🏦 Deposit Page: Account list:', response.data.accountList);
        setTransaction(response.data);

        // Check for account selection requirement first
        if (isAccountSelectionState(response.data.statusTxt)) {
          console.log('🏦 Initial load: Account selection required, showing modal with accounts:', response.data.accountList);
          setStep('account_selection');
          setShowAccountModal(true);
          return;
        }

        // Fallback for Maybank: if accounts are already available but status hasn't flipped yet
        // Only trigger if transaction already has bank credentials (not initial page load)
        if (
          response.data.bank === 'maybankfpx' &&
          Array.isArray(response.data.accountList) && response.data.accountList.length > 0 &&
          !isPostSelectionOrTerminalState(response.data.statusTxt)
        ) {
          console.log('🏦 Initial load Maybank fallback: accounts present, opening selection modal');
          setStep('account_selection');
          setShowAccountModal(true);
          // Do not return; continue evaluating status for redirects if needed
        }

        // Check transaction status and redirect if needed
        if (response.data.statusTxt === 'done') {
          setSuccess('Transaction already completed!');
          setTimeout(() => navigate('/transactions'), 3000);
          return;
        }

        // If transaction is in completed status, it means transfer form submitted but waiting for user confirmation
        if (response.data.statusTxt === 'completed') {
          setSuccess('Transfer submitted successfully! Please confirm the transfer in your bank application.');
          setStep('waiting_approval');
          startTransactionPolling(); // Continue polling for final completion
          return;
        }

        if (response.data.statusTxt === 'failed' || response.data.statusTxt === 'rejected') {
          setError('Transaction has failed or been rejected.');
          setStep('bank_selection'); // Don't show login form for failed/rejected transactions
          return;
        }

        // If transaction already has bank data, show processing step and start polling
        if (response.data.bank) {
          setSelectedBank(response.data.bank);
          setStep('processing');
          // Start polling for transaction updates
          startTransactionPolling();
        }
      } else {
        setError(response.message || 'Failed to fetch transaction details');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to fetch transaction details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankSelection = (bankId: string) => {
    // Redirect to the same URL with bank parameter
    navigate(`/deposit/${transactionId}?bank=${bankId}`);
  };





  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      // Clear any previous errors
      setStep('processing');

      // Update transaction with bank credentials
      const response = await transactionService.updateDepositTransactionData(transactionId!, {
        bank: selectedBank,
        username: username,
        password: password
      });

      if (response.success) {
        setSuccess('Bank credentials submitted successfully! Processing transaction...');

        // Start polling for transaction updates
        startTransactionPolling();
      } else {
        setError(response.message || 'Failed to submit bank credentials');
        setStep('login_form');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to submit bank credentials');
      setStep('login_form');
    } finally {
      setIsLoading(false);
    }
  };

  const startTransactionPolling = () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await transactionService.getDepositTransaction(transactionId!);

        if (response.success && response.data) {
          console.log('🔄 Polling: Transaction data received:', response.data);
          console.log('🔄 Polling: Account list:', response.data.accountList);
          setTransaction(response.data);

          // Check for account selection requirement (only if not already submitted)
          if (isAccountSelectionState(response.data.statusTxt) && !accountSelectionSubmitted) {
            clearInterval(pollInterval);
            console.log('🏦 Account selection required, showing modal with accounts:', response.data.accountList);
            setStep('account_selection');
            setShowAccountModal(true);
            return;
          }

          // Fallback for Maybank: open modal if accounts are available even if status hasn't flipped
          // But skip if user already submitted their selection
          // Only trigger if we're in processing step (user has submitted credentials)
          if (
            !accountSelectionSubmitted &&
            step === 'processing' &&
            response.data.bank === 'maybankfpx' &&
            Array.isArray(response.data.accountList) && response.data.accountList.length > 0 &&
            !showAccountModal &&
            !isPostSelectionOrTerminalState(response.data.statusTxt)
          ) {
            console.log('🏦 Polling Maybank fallback: accounts present, opening selection modal while continuing to poll for status');
            setStep('account_selection');
            setShowAccountModal(true);
            // Keep polling so we catch the status flip to waitapproval/done later
          }

          // Check for waiting approval status
          if (response.data.statusTxt === 'waitapproval') {
            setSuccess('Transfer submitted successfully! Please confirm the transfer in your bank application.');
            setStep('waiting_approval');
            return;
          }

          // Check for timeout status
          if (response.data.statusTxt === 'timeout') {
            clearInterval(pollInterval);
            setError('Transfer confirmation timeout. The transfer was not confirmed within the expected time.');
            setStep('bank_selection');
            return;
          }

          // Check if transaction is completed (waiting for approval) or done (fully completed) or failed
          if (['completed', 'done', 'failed', 'rejected'].includes(response.data.statusTxt)) {

            if (response.data.statusTxt === 'completed') {
              // Transfer form submitted, waiting for user confirmation in bank app
              setSuccess('Transfer submitted successfully! Please confirm the transfer in your bank application.');
              setStep('waiting_approval');
              // Don't clear polling interval - keep checking for final completion
              return;
            }

            if (response.data.statusTxt === 'done') {
              // Transaction fully completed
              clearInterval(pollInterval);
              setSuccess('Transaction completed successfully!');
              setStep('completed');

              // Show completion details if available
              if (response.data.paymentReference) {
                console.log('💰 Payment reference available:', response.data.paymentReference);
              }
              if (response.data.paymentId) {
                console.log('💰 Payment ID available:', response.data.paymentId);
              }

              setTimeout(() => navigate('/transactions'), 5000); // Extended time to show details
            } else if (['failed', 'rejected'].includes(response.data.statusTxt)) {
              // Transaction failed or rejected
              clearInterval(pollInterval);

              const errorMsg = response.data.statusTxt === 'failed'
                ? 'Transaction failed. Please check your bank credentials and try again.'
                : 'Transaction was rejected. Please contact support if this issue persists.';

              setError(errorMsg);
              setStep('failed');

              // Show failure details if available in logs
              if (response.data.logs && response.data.logs.length > 0) {
                const lastLog = response.data.logs[response.data.logs.length - 1];
                console.log('❌ Last transaction log:', lastLog);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error polling transaction:', error);
      }
    }, 5000); // Poll every 5 seconds (reduced frequency to avoid server load)

    // Clear interval after 5 minutes
    setTimeout(() => clearInterval(pollInterval), 300000);
  };

  const handleAccountSelection = async (accountNumber: string) => {
    try {
      setIsAccountSelectionLoading(true);
      setError('');

      // Check if transaction has already failed/timed out
      if (transaction?.statusTxt === 'failed' || transaction?.statusTxt === 'timeout' || transaction?.statusTxt === 'rejected') {
        setError('Transaction has already failed or timed out. Please start a new transaction.');
        setShowAccountModal(false);
        return;
      }

      // Find the selected account from the account list
      const selectedAccount = transaction?.accountList?.find(
        account => account.accountNumber === accountNumber
      );

      if (!selectedAccount) {
        setError('Selected account not found');
        return;
      }

      console.log('🏦 Deposit Page: Account selected:', selectedAccount);

      // Update transaction with selected account
      const response = await transactionService.updateDepositTransactionData(transactionId!, {
        accountNumber: accountNumber
      });

      if (response.success) {
        // Mark that selection has been submitted to prevent re-opening modal
        setAccountSelectionSubmitted(true);
        setShowAccountModal(false);
        setSuccess('Account selected successfully! Processing transfer...');
        setStep('processing');

        // Continue polling for transaction updates
        startTransactionPolling();
      } else {
        setError(response.message || 'Failed to select account');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to select account');
    } finally {
      setIsAccountSelectionLoading(false);
    }
  };



  const getSelectedBankInfo = () => {
    return BANK_OPTIONS.find(bank => bank.id === selectedBank);
  };

  const getDisplayCurrency = (t?: PublicDepositTransaction | null): string => {
    if (!t) return 'MYR';
    const bank = (t.bank || '').toLowerCase();
    if (bank.includes('kbankpromptpay')) return 'THB';
    // Try to infer from QR content if present
    const qr = t.qrContent || '';
    if (qr.includes('764')) return 'THB'; // ISO 4217 numeric for THB
    if (qr.includes('458')) return 'MYR'; // ISO 4217 numeric for MYR
    return 'MYR';
  };

  const formatPublicAmount = (amount: number, code: string): string => {
    try {
      const locale = code === 'MYR' ? 'en-MY' : 'en-US';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code, minimumFractionDigits: 2 }).format(amount / 100);
    } catch {
      return `RM ${(amount / 100).toFixed(2)}`;
    }
  };


  if (isLoading && !transaction) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading transaction details...</p>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Transaction Not Found</h1>
          <p className="text-gray-600 mb-4">The transaction you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/transactions')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-6">
              <strong>Success:</strong> {success}
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white shadow rounded-lg p-6">
            {step === 'bank_selection' && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Your Bank</h2>

                {/* Show different content based on transaction status */}
                {transaction && ['failed', 'rejected'].includes(transaction.statusTxt) ? (
                  <div className="text-center py-8">
                    <div className="text-red-500 text-6xl mb-4">❌</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Transaction {transaction.statusTxt}</h3>
                    <p className="text-gray-600 mb-4">
                      This transaction cannot be processed further.
                    </p>
                    <button
                      onClick={() => navigate('/transactions')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                    >
                      Back to Transactions
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-600 mb-6">Choose the bank you want to transfer from:</p>

                    <div className="space-y-4">
                      {BANK_OPTIONS.map((bank) => (
                        <button
                          key={bank.id}
                          onClick={() => handleBankSelection(bank.id)}
                          className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                        >
                          <div className="flex items-center">
                            <span className="text-3xl mr-4">{bank.icon}</span>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{bank.displayName}</h3>
                              <p className="text-gray-600">{bank.description}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 'login_form' && transaction && !['failed', 'rejected'].includes(transaction.statusTxt) && (
              <div>
                <div className="flex items-center mb-6">
                  {/* <button
                    onClick={handleBackToBankSelection}
                    className="text-blue-600 hover:text-blue-800 mr-4"
                  >
                    ← Back
                  </button> */}
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{getSelectedBankInfo()?.icon}</span>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {getSelectedBankInfo()?.displayName} Login
                      </h2>

                    </div>
                  </div>
                </div>



                <form onSubmit={handleLoginSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your bank username"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your bank password"
                      required
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <span className="text-yellow-400">⚠️</span>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Security Notice</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>Your login credentials are used only to complete this transaction and are not stored on our servers.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !username || !password}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-4 rounded-md font-medium"
                  >
                    {isLoading ? 'Processing...' : 'Submit & Process Transfer'}
                  </button>
                </form>
              </div>
            )}

            {step === 'account_selection' && (
              <div className="text-center">
                <div className="text-purple-500 text-6xl mb-4">🏦</div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Account Selection Required</h2>
                <p className="text-gray-600 mb-4">
                  Multiple accounts found for {getSelectedBankInfo()?.displayName}. Please select which account to transfer from.
                </p>

                {transaction?.accountList && transaction.accountList.length > 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                    <p className="text-sm text-green-700">
                      ✅ Found {transaction.accountList.length} account(s). The account selection modal should appear automatically.
                    </p>
                    <button
                      onClick={() => setShowAccountModal(true)}
                      className="mt-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm"
                    >
                      Show Account Selection
                    </button>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                    <p className="text-sm text-yellow-700">
                      ⏳ Loading account information...
                    </p>
                  </div>
                )}

                {transaction && (
                  <div className="mt-6 text-left">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Transaction Status</h3>
                    <div className="bg-gray-50 rounded-md p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Current Status:</span>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {transaction.statusTxt}
                        </span>
                      </div>
                      {transaction.accountList && (
                        <div className="mt-2 flex justify-between items-center">
                          <span className="text-sm text-gray-600">Available Accounts:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {transaction.accountList.length} account(s)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'processing' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Your Transfer</h2>
                <p className="text-gray-600 mb-4">
                  Please wait while we process your transfer from {getSelectedBankInfo()?.displayName}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p className="text-sm text-blue-700">
                    This may take a few minutes. Please do not close this page.
                  </p>
                </div>

                {transaction && (
                  <div className="mt-6 text-left">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Transaction Status</h3>
                    <div className="bg-gray-50 rounded-md p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Current Status:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.statusTxt === 'userlogin' ? 'bg-blue-100 text-blue-800' :
                          transaction.statusTxt === 'selectaccount' || transaction.statusTxt === 'account_selection_required' || transaction.statusTxt === 'SELECTACCOUNT' ? 'bg-purple-100 text-purple-800' :
                          transaction.statusTxt === 'waitapproval' ? 'bg-orange-100 text-orange-800' :
                          transaction.statusTxt === 'processing' ? 'bg-blue-100 text-blue-800' :
                          transaction.statusTxt === 'completed' || transaction.statusTxt === 'done' ? 'bg-green-100 text-green-800' :
                          transaction.statusTxt === 'failed' || transaction.statusTxt === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.statusTxt}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'waiting_approval' && (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">⏳</span>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Waiting for Confirmation</h2>
                  <p className="text-gray-600 mb-4">
                    Your transfer has been submitted to {getSelectedBankInfo()?.displayName}
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-yellow-400">📱</span>
                    </div>
                    <div className="ml-3 text-left">
                      <h3 className="text-sm font-medium text-yellow-800">Action Required</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>Please open your bank's mobile app or online banking to confirm this transfer.</p>
                        <p className="mt-1">You may receive a notification or SMS with a confirmation code.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p className="text-sm text-blue-700">
                    <strong>Keep this page open.</strong> We'll automatically update the status once your transfer is confirmed.
                  </p>
                </div>

                {transaction && (
                  <div className="mt-6 text-left">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Transaction Details</h3>
                    <div className="bg-gray-50 rounded-md p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Amount:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {formatPublicAmount(transaction.amount, getDisplayCurrency(transaction))}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Bank:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {getSelectedBankInfo()?.displayName}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Status:</span>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Waiting for Approval
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Transaction Completed Step */}
            {step === 'completed' && (
              <div className="text-center">
                <div className="text-green-500 text-6xl mb-4">✅</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Transaction Completed!</h2>
                <p className="text-gray-600 mb-6">
                  Your transfer has been completed successfully.
                </p>

                {transaction && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
                    <div className="text-left space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700 font-medium">Amount:</span>
                        <span className="text-sm font-semibold text-green-900">
                          {formatPublicAmount(transaction.amount, getDisplayCurrency(transaction))}
                        </span>
                      </div>
                      {transaction.bank && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-green-700 font-medium">Bank:</span>
                          <span className="text-sm font-semibold text-green-900">
                            {transaction.bank}
                          </span>
                        </div>
                      )}
                      {transaction.paymentReference && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-green-700 font-medium">Reference:</span>
                          <span className="text-sm font-mono text-green-900">
                            {transaction.paymentReference}
                          </span>
                        </div>
                      )}
                      {transaction.paymentId && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-green-700 font-medium">Payment ID:</span>
                          <span className="text-sm font-mono text-green-900">
                            {transaction.paymentId}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700 font-medium">Status:</span>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {transaction.statusTxt}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-sm text-gray-500 mb-4">
                  You will be redirected to the transactions page in a few seconds...
                </p>

                <button
                  onClick={() => navigate('/transactions')}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md"
                >
                  View All Transactions
                </button>
              </div>
            )}

            {/* Transaction Failed Step */}
            {step === 'failed' && (
              <div className="text-center">
                <div className="text-red-500 text-6xl mb-4">❌</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Transaction Failed</h2>
                <p className="text-gray-600 mb-6">
                  Unfortunately, your transaction could not be completed.
                </p>

                {transaction && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                    <div className="text-left space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-red-700 font-medium">Amount:</span>
                        <span className="text-sm font-semibold text-red-900">
                          {formatPublicAmount(transaction.amount, getDisplayCurrency(transaction))}
                        </span>
                      </div>
                      {transaction.bank && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-red-700 font-medium">Bank:</span>
                          <span className="text-sm font-semibold text-red-900">
                            {transaction.bank}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-red-700 font-medium">Status:</span>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {transaction.statusTxt}
                        </span>
                      </div>
                      {transaction.logs && transaction.logs.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-red-200">
                          <span className="text-sm text-red-700 font-medium">Last Update:</span>
                          <p className="text-sm text-red-900 mt-1">
                            {transaction.logs[transaction.logs.length - 1].message}
                          </p>
                          {transaction.logs[transaction.logs.length - 1].remark && (
                            <p className="text-xs text-red-700 mt-1">
                              {transaction.logs[transaction.logs.length - 1].remark}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setStep('bank_selection');
                      setError('');
                      setSuccess('');
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md mr-3"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate('/transactions')}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md"
                  >
                    View All Transactions
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Account Selection Modal */}
          <AccountSelectionModal
            isOpen={showAccountModal}
            onClose={() => setShowAccountModal(false)}
            onSelectAccount={handleAccountSelection}
            accounts={transaction?.accountList || []}
            transactionAmount={transaction?.amount || 0}
            bankName={getSelectedBankInfo()?.displayName || 'Bank'}
            isLoading={isAccountSelectionLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default DepositPage;
